import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { eventApi } from "~/lib/api/event";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import type { EventFormData } from "./utils";

export interface CoverImageProps {
	className?: string;
	disabled?: boolean;
	isPreview?: boolean;
}

export function CoverImage({
	className,
	disabled,
	isPreview,
}: CoverImageProps) {
	const { mutateAsync: generateCoverImage, isPending: isGenerating } =
		useMutation({
			mutationFn: async () => {
				const title = form.watch("title") || "";
				const description = form.watch("description") || undefined;
				if (!title) {
					toast.error("Title is required");
					return;
				}
				return await eventApi.generateCoverImage(title, description);
			},
			onSuccess: (data) => {
				if (!data) return;
				form.setValue("coverImageUrl", data.data.url);
				form.setValue("hasAIGeneratedCoverImage", true);
				form.setValue("coverImage", undefined);
			},
		});
	const form = useFormContext<EventFormData>();
	const coverImage = form.watch("coverImage");
	const coverImageUrl = form.watch("coverImageUrl");
	const _disabled = disabled || isPreview || isGenerating;

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: {
			"image/jpeg": [".jpg", ".jpeg"],
			"image/png": [".png"],
			"image/webp": [".webp"],
		},
		maxSize: 10 * 1024 * 1024, // 10MB
		disabled: _disabled,
		onDrop: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			// Revoke previous URL if exists
			if (coverImageUrl) {
				URL.revokeObjectURL(coverImageUrl);
			}

			// Create new URL and store both file and URL
			const url = URL.createObjectURL(file);
			form.setValue("coverImage", file);
			form.setValue("coverImageUrl", url);
			form.setValue("hasAIGeneratedCoverImage", false);
		},
		onDropRejected: (fileRejections) => {
			if (fileRejections.length > 0) {
				for (const fileRejection of fileRejections) {
					if (fileRejection.errors.length > 0) {
						const errorMessage = fileRejection.errors[0].code;
						if (errorMessage === "file-too-large") {
							toast.error(
								"File is too large. Please upload a file smaller than 10MB.",
							);
						} else if (errorMessage === "file-invalid-type") {
							toast.error(
								"Invalid file type. Please upload a valid image (jpg, png, webp).",
							);
						}
					}
				}
			}
		},
	});

	return (
		<div className={cn(className, "w-full")}>
			<div
				{...getRootProps()}
				className={cn(
					"relative aspect-square rounded-xl overflow-hidden group w-full",
					"bg-primary/10 border border-white/10",
					"hover:border-white/20 transition-colors h-[200px] md:size-[300px]",
					isPreview && "h-[200px] md:size-[300px] pointer-events-none",
					disabled && "opacity-50 pointer-events-none",
					isDragActive && "border-white/30",
					// className,
				)}
			>
				<input {...getInputProps()} />
				{coverImageUrl ? (
					<img
						src={coverImageUrl}
						alt={form.watch("title") || "Cover Image"}
						className="w-full h-full object-cover"
					/>
				) : (
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="text-center">
							<p className="text-white/70 text-sm">
								Add a cover image (optional)
							</p>
							<p className="text-white/50 text-xs mt-1">
								Recommended size: 400x400
							</p>
						</div>
					</div>
				)}

				<button
					type="button"
					className={cn(
						"absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm",
						"opacity-0 group-hover:opacity-100 transition-opacity",
						"text-white text-sm font-medium",
					)}
					disabled={_disabled}
				>
					{coverImage ? "Change Cover" : "Add Cover"}
				</button>
			</div>
			<Button
				type="button"
				className="text-white text-sm font-medium mt-4 w-full bg-primary/10 border border-white/10 hover:bg-primary/20 transition-colors md:w-[300px]"
				disabled={_disabled || isGenerating}
				onClick={() => generateCoverImage()}
				// variant="link"
			>
				{isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
				Generate AI Cover
			</Button>
		</div>
	);
}
